import { inject, Injectable, signal } from '@angular/core';
import { CrmEndpoint, CrmEndpointDecorator } from '@touch4it/ng-swp/endpoint';
import { Params } from '@angular/router';
import { BehaviorSubject, map, Observable, of, tap } from 'rxjs';
import { HttpContext } from '@angular/common/http';
import { Title } from '@angular/platform-browser';

import { ProfileModel } from '~/api/profiles/profile.model';
import { SKIP_NAMESPACE_TOKEN } from '~/shared/interceptors/skip-namespace.token';

import { ProfileCreateWorkspaceDto } from './profile-create-workspace.dto';
import { ProfileUpdateWorkspaceDto } from './profile-update-workspace.dto';

@Injectable({ providedIn: 'root' })
export class ProfilesApiService {
  @CrmEndpointDecorator({
    configName: 'crm',
    endpointName: 'profiles',
  })
  protected readonly endpoint!: CrmEndpoint<ProfileModel>;

  readonly workspaces = signal<ProfileModel[] | undefined>(undefined);

  readonly currentWorkspace$ = new BehaviorSubject<ProfileModel | undefined>(undefined);

  readonly currentWorkspaceNamespace$ = this.currentWorkspace$.pipe(map((workspace) => workspace?.namespace));

  private title = inject(Title);

  invalidate() {
    this.title.setTitle('T4 Assistant | Backoffice');
    this.currentWorkspace$.next(undefined);
    this.workspaces.set(undefined);
  }

  addToWorkspaces(workspace: ProfileModel) {
    this.workspaces.update((workspaces) => {
      const exists = workspaces?.find(({ namespace }) => namespace === workspace.namespace);

      if (exists) {
        return workspaces;
      }

      return [...(workspaces ?? []), workspace];
    });
  }

  setCurrentWorkspace(namespace: string) {
    const workspaces = this.workspaces();
    if (!workspaces) {
      throw new Error('Profiles not loaded');
    }

    const workspace = workspaces.find((model) => model.namespace === namespace);
    this.currentWorkspace$.next(workspace);
    this.title.setTitle(`${workspace?.name} | T4 Assistant | Backoffice`);
    localStorage.setItem('__sc__workspace', namespace);
  }

  getStoredWorkspace() {
    return localStorage.getItem('__sc__workspace');
  }

  listAllWorkspaces(params?: Params) {
    const workspaces = this.workspaces();

    if (workspaces?.length) {
      return of(workspaces);
    }

    return this.endpoint
      .listAll({
        params: { ...params, type: 'workspace' },
        context: new HttpContext().set(SKIP_NAMESPACE_TOKEN, true),
      })
      .pipe(tap((data) => this.workspaces.set(data)));
  }

  getCurrentWorkspace() {
    const workspace = this.currentWorkspace$.value;

    if (!workspace) {
      throw new Error('current workspace undefined!');
    }

    return this.getWorkspace(workspace._id);
  }

  getWorkspace(id: string) {
    const workspaces = this.workspaces();

    if (workspaces?.length) {
      const workspace = workspaces.find((workspace) => workspace._id === id);

      if (workspace) {
        return of(workspace);
      }
    }

    return this.endpoint
      .read(id, { context: new HttpContext().set(SKIP_NAMESPACE_TOKEN, true) })
      .pipe(tap((workspace) => this.addToWorkspaces(workspace)));
  }

  createWorkspace(body: ProfileCreateWorkspaceDto): Observable<ProfileModel> {
    return this.endpoint
      .create({ ...body, type: 'workspace' }, { context: new HttpContext().set(SKIP_NAMESPACE_TOKEN, true) })
      .pipe(
        tap((workspace) => {
          this.addToWorkspaces(workspace);
          this.setCurrentWorkspace(workspace.namespace);
        }),
      );
  }

  updateWorkspace(id: string, body: ProfileUpdateWorkspaceDto): Observable<ProfileModel> {
    return this.endpoint
      .update(id, body, {
        context: new HttpContext().set(SKIP_NAMESPACE_TOKEN, true),
      })
      .pipe(
        tap((workspace) => {
          this.workspaces.update((workspaces) => {
            return (workspaces ?? []).map((loadedWorkspace) => {
              return loadedWorkspace._id === workspace._id ? workspace : loadedWorkspace;
            });
          });
          this.setCurrentWorkspace(workspace.namespace);
        }),
      );
  }
}
